import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  computeCoverage,
  TRACKED_SYNC_TYPES,
  type DBSyncLog,
  type ProjectMeta,
  type SyncCoverage,
  type SyncSource,
} from "@/lib/sync-coverage-calc";

// Re-eksport så eksisterende forbrukere (komponenter, tester) kan importere
// fra `@/hooks/use-sync-coverage` uavhengig av om de trenger Supabase-klienten
// eller ikke.
export {
  computeCoverage,
  TRACKED_SYNC_TYPES,
} from "@/lib/sync-coverage-calc";
export type {
  DBSyncLog,
  GapWarning,
  ProjectMeta,
  SyncCoverage,
  SyncHealthStatus,
  SyncMetadata,
  SyncSource,
  SyncTriggerSource,
} from "@/lib/sync-coverage-calc";

/**
 * `useSyncCoverage` aggregerer rader fra `sync_log` og rapporterer
 * dekning per (source, entity_type). Brukes av Importdekning-seksjonen i
 * Innstillinger til å vise hvilket datointervall som er importert,
 * når sist kjørt, og hvilke prosjekter som potensielt mangler historikk.
 */
export function useSyncCoverage() {
  return useQuery({
    queryKey: ["sync-coverage"],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<SyncCoverage[]> => {
      const [logsRes, projectsRes] = await Promise.all([
        supabase
          .from("sync_log")
          .select("*")
          .order("started_at", { ascending: false })
          .limit(500),
        supabase
          .from("projects")
          .select("id, name, project_code, created_at")
          .is("deleted_at", null),
      ]);

      if (logsRes.error) throw logsRes.error;
      if (projectsRes.error) throw projectsRes.error;

      const logs = (logsRes.data ?? []) as DBSyncLog[];
      const projects = (projectsRes.data ?? []) as ProjectMeta[];

      return TRACKED_SYNC_TYPES.map(({ source, entity_type }) =>
        computeCoverage(source, entity_type, logs, projects),
      );
    },
  });
}

export function useSyncLogHistory(filter: {
  source?: SyncSource;
  entity_type?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["sync-log-history", filter],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<DBSyncLog[]> => {
      let query = supabase
        .from("sync_log")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(filter.limit ?? 50);
      if (filter.source) query = query.eq("source", filter.source);
      if (filter.entity_type) query = query.eq("entity_type", filter.entity_type);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as DBSyncLog[];
    },
  });
}
